import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = { name:'', code:'', company_id:'', status:'active' };

export default function Units() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [units, setUnits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('');

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/units'); setUnits(r.data); }
    catch (err) { console.error('units:', err); setUnits([]); }
    try { const r = await api.get('/api/companies'); setCompanies(r.data); }
    catch (err) { console.error('companies:', err); setCompanies([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const filtered = units.filter(u => {
    const matchSearch = u.name.toLowerCase().includes(search.toLowerCase()) ||
      (u.code||'').toLowerCase().includes(search.toLowerCase());
    const matchCompany = !filterCompany || String(u.company_id) === filterCompany;
    return matchSearch && matchCompany;
  });

  const getCompanyName = (id) => companies.find(c => c.id === id)?.name || '—';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, company_id: form.company_id ? Number(form.company_id) : null };
      if (editing) await api.put(`/api/units/${editing}`, payload);
      else await api.post('/api/units', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (u) => {
    setForm({ name:u.name, code:u.code||'', company_id:u.company_id||'', status:u.status||'active' });
    setEditing(u.id); setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!confirmDelete('unit', name)) return;
    try { await api.delete(`/api/units/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Unit Management</h1>
          <p className="text-gray-500 text-sm">{units.length} units registered</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ Add Unit
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3">
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or code..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]" />
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Unit' : 'Add Unit'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({...form,name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Code *</label>
                <input type="text" value={form.code} onChange={e => setForm({...form,code:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm({...form,status:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select value={form.company_id} onChange={e => setForm({...form,company_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Unit'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">🏭</div>
            <p>Loading units...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">🏭</div>
            <p>No units found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Unit Name','Code','Company','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{u.code}</td>
                    <td className="px-4 py-3 text-gray-600">{getCompanyName(u.company_id)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(u)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️ Edit</button>
                        {isAdmin && <button onClick={() => handleDelete(u.id, u.name)} className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️ Delete</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
